import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BugReportIcon from "@mui/icons-material/BugReport";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { INBOUND_PAYLOAD_STATUSES } from "../constants/admin";
import { useApi } from "../hooks/useApi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function SquarespacePayloads() {
  const navigate = useNavigate();
  const api = useApi();
  const [payloads, setPayloads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sourceFilter, setSourceFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [debugPayload, setDebugPayload] = React.useState(null);
  const [debugArrayOpen, setDebugArrayOpen] = React.useState(false);
  const [cronLoading, setCronLoading] = React.useState(false);

  const fetchPayloads = React.useCallback(
    async (search = "", source = "", status = "", offset = 0) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (search?.trim()) params.append("search", search.trim());
        if (source) params.append("source", source);
        if (status) params.append("status", status);
        params.append("limit", pagination.limit.toString());
        params.append("offset", offset.toString());

        const data = await api.get(`/admin/squarespace/payloads?${params.toString()}`);
        setPayloads(data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data.pagination?.total || 0,
          offset: data.pagination?.offset ?? offset,
          page: Math.floor(offset / pagination.limit) + 1,
        }));
      } catch (err) {
        console.error("Error fetching payloads:", err);
        setError(err.message || "Failed to fetch payloads");
      } finally {
        setLoading(false);
      }
    },
    [api, pagination.limit]
  );

  React.useEffect(() => {
    fetchPayloads(searchQuery, sourceFilter, statusFilter, pagination.offset);
  }, [fetchPayloads]);

  const handleTriggerCron = React.useCallback(async () => {
    setCronLoading(true);
    setError(null);
    try {
      await api.post("/admin/squarespace/payloads/trigger-cron", {});
      setError(null);
      await fetchPayloads(searchQuery, sourceFilter, statusFilter, pagination.offset);
      // Show success - could use a snackbar; for now rely on refresh
    } catch (err) {
      setError(err.message || "Failed to trigger cron");
    } finally {
      setCronLoading(false);
    }
  }, [api, searchQuery, sourceFilter, statusFilter, pagination.offset, fetchPayloads]);

  const handleSearch = () => {
    setPagination((p) => ({ ...p, offset: 0 }));
    fetchPayloads(searchQuery, sourceFilter, statusFilter, 0);
  };

  const handlePageChange = (_, page) => {
    const offset = (page - 1) * pagination.limit;
    setPagination((p) => ({ ...p, page, offset }));
    fetchPayloads(searchQuery, sourceFilter, statusFilter, offset);
  };

  const handleDelete = async (payload) => {
    if (!window.confirm(`Delete payload ${payload.id}?`)) return;
    try {
      await api.delete(`/admin/squarespace/payloads/${payload.id}`);
      await fetchPayloads(searchQuery, sourceFilter, statusFilter, pagination.offset);
    } catch (err) {
      setError(err.message || "Failed to delete payload");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "default";
    const s = status.toLowerCase();
    if (s === "complete") return "success";
    if (s === "processing" || s === "new") return "info";
    if (s === "failed") return "error";
    return "default";
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{ textDecoration: "none", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{ textDecoration: "none", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Payloads
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Inbound Payloads"
              description="Manage inbound webhook payloads"
              icon={<StorageIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={cronLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={handleTriggerCron}
                disabled={cronLoading}
              >
                {cronLoading ? "Running..." : "Run Cron"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<BugReportIcon />}
                onClick={() => setDebugArrayOpen(true)}
              >
                JSON Debug (all)
              </Button>
            </SquarespacePageHeader>

            <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  label="Source"
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="squarespace">Squarespace</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {INBOUND_PAYLOAD_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={handleSearch}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/dashboard/squarespace/payloads/new")}
              >
                Add Payload
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        <TableCell><Skeleton height={24} width={200} /></TableCell>
                        <TableCell><Skeleton height={24} width={80} /></TableCell>
                        <TableCell><Skeleton height={24} width={60} /></TableCell>
                        <TableCell><Skeleton height={24} width={120} /></TableCell>
                        <TableCell><Skeleton height={24} width={80} /></TableCell>
                      </TableRow>
                    ))
                  ) : payloads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No payloads found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    payloads.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                          {p.id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell>{p.source || "—"}</TableCell>
                        <TableCell>
                          <Chip label={p.status || "—"} size="small" color={getStatusColor(p.status)} />
                        </TableCell>
                        <TableCell>{formatDate(p.created_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => setDebugPayload(p)}
                            title="JSON Debug"
                          >
                            <BugReportIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/dashboard/squarespace/payloads/${p.id}`)}
                            title="View / Edit"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(p)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination.total > pagination.limit && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.limit)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>

      <Dialog
        open={!!debugPayload}
        onClose={() => setDebugPayload(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "60vh" } }}
      >
        <DialogTitle>JSON Debug</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            value={
              debugPayload
                ? JSON.stringify(
                    {
                      id: debugPayload.id,
                      source: debugPayload.source,
                      status: debugPayload.status,
                      payload: debugPayload.payload,
                      notes: debugPayload.notes,
                      created_at: debugPayload.created_at,
                      updated_at: debugPayload.updated_at,
                    },
                    null,
                    2
                  )
                : ""
            }
            InputProps={{ readOnly: true }}
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              mt: 1,
              "& .MuiInputBase-input": { whiteSpace: "pre-wrap", wordBreak: "break-all" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (debugPayload) {
                navigator.clipboard.writeText(
                  JSON.stringify(
                    {
                      id: debugPayload.id,
                      source: debugPayload.source,
                      status: debugPayload.status,
                      payload: debugPayload.payload,
                      notes: debugPayload.notes,
                      created_at: debugPayload.created_at,
                      updated_at: debugPayload.updated_at,
                    },
                    null,
                    2
                  )
                );
              }
            }}
          >
            Copy to clipboard
          </Button>
          <Button onClick={() => setDebugPayload(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={debugArrayOpen}
        onClose={() => setDebugArrayOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "60vh" } }}
      >
        <DialogTitle>JSON Debug – All Records</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            value={JSON.stringify(
              {
                data: payloads,
                pagination: {
                  total: pagination.total,
                  limit: pagination.limit,
                  offset: pagination.offset,
                  page: pagination.page,
                },
              },
              null,
              2
            )}
            InputProps={{ readOnly: true }}
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              mt: 1,
              "& .MuiInputBase-input": { whiteSpace: "pre-wrap", wordBreak: "break-all" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(
                JSON.stringify(
                  {
                    data: payloads,
                    pagination: {
                      total: pagination.total,
                      limit: pagination.limit,
                      offset: pagination.offset,
                      page: pagination.page,
                    },
                  },
                  null,
                  2
                )
              );
            }}
          >
            Copy to clipboard
          </Button>
          <Button onClick={() => setDebugArrayOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
